//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import { FileRepository, Plugin } from "ckeditor5";
import Messenger from "messaging/messenger";

class Adapter {
  constructor(loader, editor) {
    this.loader = loader;
    this.editor = editor;
  }

  upload() {
    return this.loader.file.then(
      (file) =>
        new Promise((resolve, reject) => {
          const csrfToken = Messenger.reqres.request("get:csrfToken");
          this._createXhr(csrfToken);
          this._addListeners(file, resolve, reject);
          this._sendRequest(file);
        }),
    );
  }

  abort() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }

  _addListeners(file, resolve, reject) {
    const xhr = this.xhr;
    const defaultErrorMessage = i18next.t("topic.response.uploadFailed", {
      file: file.name,
    });
    xhr.addEventListener("error", () => reject(defaultErrorMessage));
    xhr.addEventListener("abort", () => reject());
    xhr.addEventListener("load", () => {
      const response = xhr.response;

      if (!response || response.status >= 400) {
        return reject(Messenger.getErrorFromResponse(response, defaultErrorMessage));
      }
      resolve({
        ...response,
        urls: { default: response.url },
      });
    });

    if (xhr.upload) {
      xhr.upload.addEventListener("progress", (evt) => {
        if (evt.lengthComputable) {
          this.loader.uploadTotal = evt.total;
          this.loader.uploaded = evt.loaded;
        }
      });
    }
  }

  _createXhr(csrfToken) {
    const authorizationHeader = Messenger.reqres.request("authorization:header");
    const url = Messenger.reqres.request("get:uploadUrl");
    if (!url) {
      console.error("No upload URL");
      return;
    }
    const xhr = (this.xhr = new XMLHttpRequest());

    xhr.open("POST", url, true);
    if (authorizationHeader) {
      xhr.setRequestHeader("Authorization", authorizationHeader);
    }
    if (csrfToken) {
      xhr.setRequestHeader("X-XSRF-TOKEN", csrfToken);
    }
    xhr.responseType = "json";
  }

  _sendRequest(file) {
    const formData = new FormData();
    const kbId = this.editor.config.get("ak.kbId");

    formData.append("file", file, file.name);
    formData.append("touchPointId", this.editor.config.get("touchPointId"));
    formData.append("languageId", this.editor.config.get("languageId"));
    formData.append("kbId", kbId);

    this.xhr.send(formData);
  }
}

export default class AkUploadAdapter extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [FileRepository];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "AkUploadAdapter";
  }

  init() {
    this.editor.plugins.get(FileRepository).createUploadAdapter = (loader) => {
      return new Adapter(loader, this.editor);
    };
  }
}
